import {
  CylinderGeometry,
  Group,
  Mesh,
  MeshLambertMaterial,
  Object3D,
  Quaternion,
  Scene,
  Vector3,
} from 'three'
import type { Pilot } from '../physics/Pilot'
import type { PilotInput } from '../input/types'
import { PilotAvatar } from './avatar'

/**
 * The XR rig: the node that parents the reference space. Its pose comes from
 * the rigid body, pivoting rotations around the estimated CoM (DESIGN.md §2).
 *
 * Coupling is strictly one-way — physics moves the rig, and the tracked head
 * and hands render exactly where the player's real limbs are inside it. The
 * virtual body reacts to thrust; the hands stay player-authoritative.
 */
export class PilotRig {
  /** Everything in tracking space hangs off this. */
  readonly group = new Group()
  /** The camera parent for XR; the headset pose is applied relative to it. */
  readonly cameraHolder = new Group()

  readonly nozzles: [Object3D, Object3D]
  readonly avatar = new PilotAvatar()
  /** Third-person view shows the head; in XR you are inside it. */
  showBody = true

  constructor(scene: Scene) {
    this.group.add(this.cameraHolder)

    this.nozzles = [buildNozzle(0xff8a5c), buildNozzle(0x6fd0ff)]
    this.group.add(this.nozzles[0], this.nozzles[1])

    this.group.add(this.avatar.group)

    scene.add(this.group)
  }

  /** Place the rig from the physics, and the hands from the tracked input. */
  update(pilot: Pilot, input: PilotInput, dt: number): void {
    pilot.getRigTransform(_position, _quaternion)
    this.group.position.copy(_position)
    this.group.quaternion.copy(_quaternion)

    for (let i = 0; i < 2; i++) {
      const hand = input.hands[i]
      const nozzle = this.nozzles[i]
      nozzle.position.copy(hand.position)
      nozzle.quaternion.copy(hand.quaternion)
      // Fade a rocket out while its controller is untracked, so the decaying
      // thrust has a visible cause.
      nozzle.visible = hand.tracked || pilot.rockets[i].trackingConfidence > 0.05
    }

    this.avatar.setFirstPerson(!this.showBody)
    this.avatar.update(pilot, input, dt)
  }
}

/**
 * A stubby rocket nozzle. Modelled pointing along −Z so it matches the
 * controller's aim ray: exhaust leaves −Z, thrust pushes +Z (§2).
 */
function buildNozzle(colour: number): Object3D {
  const group = new Group()

  const body = new Mesh(
    new CylinderGeometry(0.045, 0.055, 0.19, 10),
    new MeshLambertMaterial({ color: 0x2c3644 }),
  )
  body.rotation.x = Math.PI / 2
  group.add(body)

  const bell = new Mesh(
    new CylinderGeometry(0.055, 0.095, 0.11, 12, 1, true),
    new MeshLambertMaterial({ color: colour, emissive: colour, emissiveIntensity: 0.25 }),
  )
  bell.rotation.x = Math.PI / 2
  bell.position.z = -0.15
  group.add(bell)

  return group
}

const _position = new Vector3()
const _quaternion = new Quaternion()
